import { Box, Dialog, DialogActions, DialogContent, LinearProgress } from "@mui/material"
import type { FC } from "react"

import { filterOutCurrentProduct } from "../utils/filterOutCurrentProduct"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { EquivalencyGroup } from "@/graphql/codegen/graphql"
import { AssociatedProductsTable } from "@/screens/ManageProducts/components/EquivalentProductGroups/components/AssociatedProductsTable"
import useEquivalencyGroupProducts from "@/screens/ManageProducts/components/EquivalentProductGroups/hooks/useEquivalencyGroupProducts"
import { useRemoveProductFromEquivalencyGroup } from "@/screens/ManageProducts/components/EquivalentProductGroups/hooks/useRemoveProductFromEquivalencyGroup"

interface ProductEquivalentGroupModalProps {
  group: EquivalencyGroup
  currentProductId: number
  onClose: () => void
  onSuccess?: () => void
}

export const ProductEquivalentGroupModal: FC<ProductEquivalentGroupModalProps> = ({
  group,
  currentProductId,
  onClose,
  onSuccess,
}) => {
  const { products, competitorProducts, fetching, refetchProductsList } = useEquivalencyGroupProducts({
    equivalencyGroupId: group.equivalencyGroupId,
  })

  const { filteredProducts, filteredCompetitorProducts, isOnlyMember } = filterOutCurrentProduct(
    products,
    competitorProducts,
    currentProductId
  )

  const { removeProduct } = useRemoveProductFromEquivalencyGroup({
    equivalencyGroupId: group.equivalencyGroupId,
    onSuccess: () => {
      refetchProductsList()
      onSuccess?.()
    },
  })

  const hasNoOtherProducts = !fetching && isOnlyMember

  return (
    <Dialog open onClose={onClose} maxWidth='lg' fullWidth data-testid='product-equivalent-group-modal'>
      <DialogHeader title={`Equivalent Product Groups: ${group.name}`} onClose={onClose} />

      <DialogContent classes={{ root: "p-10" }}>
        {fetching && (
          <Box className='py-4'>
            <LinearProgress />
          </Box>
        )}

        {!fetching && hasNoOtherProducts && (
          <InformationMissingAlert
            title='This product is the only member of this group'
            data-testid='only-member-message'
          />
        )}

        {!fetching && !hasNoOtherProducts && (
          <AssociatedProductsTable
            akrochemProducts={filteredProducts}
            competitorProducts={filteredCompetitorProducts}
            onRemove={removeProduct}
            onRowClick={onClose}
          />
        )}
      </DialogContent>

      <DialogActions className='px-10 pb-6'>
        <Button appearance='outlined' onClick={onClose} data-testid='close-modal-button'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
